import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon, ViewListIcon } from "@heroicons/react/solid";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  clearAccountingTransaction,
  requestAddMainBusiness,
  requestDeleteAcctTransaction,
  requestListAccountingTransaction,
  requestListBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../Context";
import {
  cancelAPIRequest,
  classNames,
  convertToTitleCase,
  getPeriodClosedError,
} from "../../Utils";
import PeriodLockBanner from "../../Components/v2/Accounting/PeriodLockBanner";
import PeriodLockCell from "../../Components/v2/Accounting/PeriodLockCell";
import PeriodClosedNotice from "../../Components/v2/Accounting/PeriodClosedNotice";
import { PencilAltIcon, TrashIcon, EyeIcon } from "@heroicons/react/outline";
import SwitchLayoutBanner from "../../Components/SwitchLayoutBanner";
import { NumericFormat } from "react-number-format";
import { useLocation, useNavigate } from "react-router-dom";
import { Pagination } from "../../Components/Pagination";
import { ModalAlert } from "../../Components/ModalAlert";
import { Alert } from "../../Components/Alert";
import { reportRoutes, REPORTS } from "../../Constants";
import { TransactionFilter } from "../../Components/TransactionFilter";
import TableSkeleton from "../../Components/TableSkeleton";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Badge from "../../Components/Badge";
import { TRANSACTION_OPTIONS, dateFilterOptions } from "../../Constants";
import EmptyBusiness from "../../Components/EmptyBusiness";
import { isBusinessMainBlocked } from "../../Constants/business";
dayjs.extend(customParseFormat);

export const AccountingTransactionList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const [selectedBusiness, setSelectedBusiness] = useState({});
  const [selectedReport, setselectedReport] = useState(null);
  const [selectedOption, setselectedOption] = useState(null);
  const [page, setpage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [transactionID, setTransactionID] = useState("");
  const [date, setDate] = useState(new Date());
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [createdAt, setcreatedAt] = useState({});
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [showAccountingV2Banner, setshowAccountingV2Banner] = useState(true);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");

  const dispatch = useBusinessTransactionDispatch();
  const accountingDispatch = useAccountingDispatch();
  const authDispatch = useAuthDispatch();

  // Resolved from the selection the page already made, not from mainBusiness:
  // this route is routinely opened with an explicit ?id= for a different
  // business, and locking the wrong one is worse than not locking at all.
  const periodBusinessID = selectedBusiness?.value || selectedBusiness?.id;
  const { isDateClosed, refetch: refetchPeriod } =
    usePeriodLock(periodBusinessID);

  const { businesses, loading } = useBusinessTransactionState();
  const {
    request,
    transactions,
    error,
    nextTokens,
    submitLoading,
    submitError,
    loading: accountingLoading,
    deletedID,
    transactionInitialData,
  } = useAccountingState();

  useEffect(() => {
    clearAccountingTransaction(accountingDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBusiness?.id) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (selectedBusiness?.value && selectedReport?.value) {
      if (reportRoutes[selectedReport?.value]) {
        const route = `${reportRoutes[selectedReport.value]}?id=${
          selectedBusiness.value
        }`;
        navigate(route);
      }
      setselectedReport(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.value, selectedReport?.value]);

  useEffect(() => {
    if (selectedBusiness?.value && selectedOption?.value) {
      const route = `/${selectedOption?.value}/create?business=${selectedBusiness.value}`;
      navigate(route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.value, selectedOption?.value]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(dispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [dispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam,
          );
          if (business) {
            setSelectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setSelectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setSelectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setSelectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedID]);

  useEffect(() => {
    if (transactionInitialData?.dateFilter) {
      setSelectedFilter(dateFilterOptions[1]);
      setDateRange([
        transactionInitialData?.startDate,
        transactionInitialData?.endDate,
      ]);
    } else {
      setSelectedFilter(dateFilterOptions[0]);
    }
  }, [transactionInitialData]);

  useEffect(() => {
    if (selectedFilter?.value === "MONTHLY") {
      setcreatedAt({
        between: [
          dayjs(date).startOf("month").toISOString(),
          dayjs(date).endOf("month").toISOString(),
        ],
      });
    }
    if (selectedFilter?.value === "OTHER") {
      setcreatedAt({
        between: [
          dayjs(startDate?.toISOString()).startOf("day").toISOString(),
          dayjs(endDate?.toISOString()).endOf("day").toISOString(),
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter?.value, date, dateRange]);

  useEffect(() => {
    const version = localStorage.getItem("accounting_version");
    if (version === "v2") {
      navigate("/v2/accounting");
    }
  }, [navigate]);

  const onLoad = () => {
    if (request) {
      cancelAPIRequest(request);
    }
    if (selectedBusiness?.id) {
      setpage(1);
      requestListAccountingTransaction(accountingDispatch, {
        page: 1,
        limit: 10,
        businessID: selectedBusiness.id,
        createdAt,
      });
    }
  };

  const onNext = () => {
    const nextPage = page + 1;
    setpage(nextPage);
    if (nextTokens[nextPage]) {
      requestListAccountingTransaction(accountingDispatch, {
        page: nextPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextToken: nextTokens[nextPage],
        createdAt,
      });
    }
  };

  const onPrev = () => {
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListAccountingTransaction(accountingDispatch, {
        page: prevPage,
        businessID: selectedBusiness.value,
        nextToken: nextTokens[prevPage],
        createdAt,
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListAccountingTransaction(accountingDispatch, {
        page: selectedPage,
        businessID: selectedBusiness.value,
        nextToken: nextTokens[selectedPage],
        createdAt,
      });
    }
  };

  const onUpdate = (transactionID, createdAtValue) => {
    if (isDateClosed(createdAtValue)) return;
    navigate(`/accounting-transaction/create?id=${transactionID}`);
  };

  const onDelete = () => {
    requestDeleteAcctTransaction(accountingDispatch, {
      id: transactionID,
    });
  };

  const handleSwitchToNewLayout = () => {
    localStorage.setItem("accounting_version", "v2");
    navigate("/v2/accounting");
  };

  const onNavigateToBusiness = () => {
    navigate("/business");
  };

  const onNavigateToEditBusiness = (id) => {
    if (id) {
      navigate(`/business/${id}`);
    }
  };

  if (user.mainBusinessID || businesses.length > 0) {
    return (
      <div className="space-y-4">
        <PeriodLockBanner businessID={periodBusinessID} />
        {
          <ModalAlert
            open={showConfirmModal}
            setOpen={(open) => {
              setShowConfirmModal(open);
            }}
            title="Delete Transaction"
            description="Are you sure you want to delete this accounting transaction?"
            confirmButtonText="Yes"
            cancelButtonText="Cancel"
            loadingButton={submitLoading}
            disabledButton={submitLoading}
            onConfirm={onDelete}
          />
        }
        {
          <ModalConfirm
            open={showSuccessModal}
            type="SUCCESS"
            setOpen={(open) => {
              setShowSuccessModal(open);
            }}
            title="Success"
            description="The accounting transaction has been successfully deleted"
            onConfirm={() => clearAccountingTransaction(accountingDispatch)}
          />
        }
        {showAccountingV2Banner && (
          <SwitchLayoutBanner
            labelKey="switchLayoutLabels.newLayout"
            onSwitch={handleSwitchToNewLayout}
            onDismiss={() => setshowAccountingV2Banner(false)}
            variant="dark"
            direction="forward"
          />
        )}
        <TransactionFilter
          selectedBusiness={selectedBusiness}
          setSelectedBusiness={setSelectedBusiness}
          date={date}
          setDate={setDate}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
          dateRange={dateRange}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          createdAt={createdAt}
          filterDate="range"
          onLoad={onLoad}
        >
          <div className="flex flex-col flex-wrap sm:flex-row gap-4">
            <Menu as="div" className="relative">
              <MenuButton className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                {t("Business")}
                <SelectorIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </MenuButton>

              <MenuItems className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="py-1">
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={() =>
                          onNavigateToEditBusiness(selectedBusiness.id)
                        }
                        className={`${
                          focus ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } flex items-center w-full px-4 py-2 text-sm`}
                        data-testid="edit-business"
                      >
                        <PencilAltIcon className="w-5 h-5 mr-3" />
                        {t("Edit Business")}
                      </button>
                    )}
                  </MenuItem>

                  <MenuItem>
                    {({ focus }) => (
                      <button
                        onClick={onNavigateToBusiness}
                        className={`${
                          focus ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } flex items-center w-full px-4 py-2 text-sm`}
                        data-testid="manage-business"
                      >
                        <ViewListIcon className="w-5 h-5 mr-3" />
                        {t("See All")}
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
            <div>
              <Listbox value={selectedReport} onChange={setselectedReport}>
                {({ open }) => (
                  <>
                    <ListboxButton className="flex items-center justify-between w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                      <span className="ml-3 mr-2">
                        {t(selectedReport?.label || t("Report"))}
                      </span>
                      <SelectorIcon className="w-5 h-5 text-gray-500" />
                    </ListboxButton>
                    <Transition
                      show={open}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                        {REPORTS.map((report) => (
                          <ListboxOption
                            key={report.value}
                            className={({ focus }) =>
                              classNames(
                                focus
                                  ? "text-white bg-blue-600"
                                  : "text-gray-900",
                                "cursor-default select-none relative py-2 pl-3 pr-9",
                              )
                            }
                            value={report}
                          >
                            {({ selected, focus }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span
                                    className={classNames(
                                      selected
                                        ? "font-semibold"
                                        : "font-normal",
                                      "ml-3 mr-2 block truncate",
                                    )}
                                  >
                                    {t(report.label)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        focus ? "text-white" : "text-blue-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4",
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
            <div>
              <Listbox value={selectedOption} onChange={setselectedOption}>
                {({ open }) => (
                  <>
                    <ListboxButton className="flex items-center justify-between w-full sm:w-fit py-2 px-3 border border-gray-300 text-white bg-blue-600 rounded-md shadow-sm text-sm font-medium">
                      <span className="ml-3 mr-2">
                        {t(selectedOption?.label || t("Create New"))}
                      </span>
                      <SelectorIcon className="w-5 h-5 text-white" />
                    </ListboxButton>
                    <Transition
                      show={open}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                        {TRANSACTION_OPTIONS.map((option) => (
                          <ListboxOption
                            key={option.value}
                            className={({ focus }) =>
                              classNames(
                                focus
                                  ? "text-white bg-blue-600"
                                  : "text-gray-900",
                                "cursor-default select-none relative py-2 pl-3 pr-9",
                              )
                            }
                            value={option}
                          >
                            {({ selected, focus }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <span
                                    className={classNames(
                                      selected
                                        ? "font-semibold"
                                        : "font-normal",
                                      "ml-3 mr-2 block truncate",
                                    )}
                                  >
                                    {t(option.label)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={classNames(
                                        focus ? "text-white" : "text-blue-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4",
                                      )}
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </>
                )}
              </Listbox>
            </div>
          </div>
        </TransactionFilter>
        <div className="flex flex-col">
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                {submitError && (
                  <div className="bg-white px-4 py-6 sm:px-6">
                    <PeriodClosedNotice
                      error={submitError}
                      onRefresh={refetchPeriod}
                    />
                    {!getPeriodClosedError(submitError) && (
                      <Alert
                        title="Error"
                        type="error"
                        message={
                          submitError?.message ||
                          "submitError delete Transaction."
                        }
                        errors={submitError?.errors}
                      />
                    )}
                  </div>
                )}
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Date")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Note")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Type")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                        {t("Total")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Action")}
                      </th>
                    </tr>
                  </thead>
                  {accountingLoading ? (
                    <TableSkeleton rows={3} columns={5} />
                  ) : (
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {selectedBusiness && transactions.length > 0 ? (
                        transactions.map((transaction) => {
                          return (
                            <tr key={transaction.id}>
                              <th className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                                {dayjs(transaction.createdAt)
                                  .locale(i18n.language)
                                  .format("DD MMM YYYY")}
                              </th>
                              <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                                {transaction.description}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                <div className="flex gap-2">
                                  <div>
                                    {t(convertToTitleCase(transaction.type))}
                                  </div>
                                  {transaction.paid &&
                                    [
                                      "INCOME_RECEIVABLE",
                                      "EXPENSE_PAYABLE",
                                      "PAYABLE",
                                      "RECEIVABLE",
                                    ].includes(transaction.type) && (
                                      <Badge title={t("Paid")} type="success" />
                                    )}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                <NumericFormat
                                  displayType={"text"}
                                  value={transaction.total}
                                  thousandSeparator={"."}
                                  decimalSeparator={","}
                                  prefix={"Rp"}
                                />
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {isDateClosed(transaction.createdAt) ? (
                                  // One lock rather than two greyed-out icons.
                                  // Invoice rows keep their read-only route via
                                  // onView — viewing was never a write.
                                  <PeriodLockCell
                                    onView={
                                      transaction.invoiceNumber &&
                                      transaction?.items?.items
                                        ? () =>
                                            navigate(
                                              `/invoice/${transaction.id}`,
                                            )
                                        : undefined
                                    }
                                  />
                                ) : (
                                <div className="flex flex-auto gap-2">
                                  {transaction.invoiceNumber &&
                                  transaction?.items?.items ? (
                                    <button
                                      onClick={() => {
                                        navigate(`/invoice/${transaction.id}`);
                                      }}
                                    >
                                      <EyeIcon className="h-5 w-5 text-blue-500" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        onUpdate(
                                          transaction.id,
                                          transaction.createdAt,
                                        )
                                      }
                                    >
                                      <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                    </button>
                                  )}

                                  <button
                                    onClick={() => {
                                      setShowConfirmModal(true);
                                      setTransactionID(transaction.id);
                                    }}
                                  >
                                    <TrashIcon className="h-5 w-5 text-red-500" />
                                  </button>
                                </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <ErrorStateTable error={error} />
                      )}
                    </tbody>
                  )}
                </table>
                <Pagination
                  page={page}
                  onPrev={onPrev}
                  onNext={onNext}
                  onPageChange={onPageChange}
                  nextTokens={nextTokens}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <EmptyBusiness loading={loading} />;
  }
};
